import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  AlertTriangle,
  BellRing,
  Bot,
  Briefcase,
  CheckCircle2,
  ExternalLink,
  FileWarning,
  History,
  Hourglass,
  Link2Off,
  RefreshCw,
  Send,
  ShieldAlert,
  Sparkles,
  UserRound,
  Users,
} from 'lucide-react';
import { FileKind, PortalStatusDetail, PositionStage, SubmissionStatus, WaitingOn } from '@clawhire/shared';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-client';
import { EMPTY, formatDate, formatDateTime, formatRelativeDays } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button, buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '@/components/ui/page-header';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState } from '@/components/ui/empty-state';
import { EnumBadge } from '@/components/data/EnumBadge';
import {
  DashboardSection,
  EntityLink,
  SectionHead,
  SectionTable,
  Td,
  Th,
} from '@/pages/dashboard/DashboardSection';
import type { Dashboard } from '@/pages/dashboard/types';

const EMPTY_DASHBOARD: Dashboard = {
  chaseList: [],
  urgentPositions: [],
  closedOnPortalButActive: [],
  sourcingWithoutJd: [],
  waitingOnUs: [],
  consentsExpiring: [],
  brokenLinks: [],
  recentActivity: [],
  counts: { openPositions: 0, activeCandidates: 0, submissionsInProgress: 0, needsChase: 0 },
  generatedAt: '',
};

function positionLabel(p: { requestNo: string; role: string }): string {
  return `${p.requestNo} ${p.role}`;
}

function OverdueBadge({ days }: { days: number }) {
  if (days > 0) {
    return (
      <Badge className="border-transparent bg-rose-100 text-rose-800 shadow-none tabular-nums dark:bg-rose-950 dark:text-rose-300">
        {days} {days === 1 ? 'day' : 'days'} overdue
      </Badge>
    );
  }
  if (days === 0) {
    return (
      <Badge className="border-transparent bg-amber-100 text-amber-800 shadow-none dark:bg-amber-950 dark:text-amber-300">
        due today
      </Badge>
    );
  }
  return (
    <span className="text-xs text-muted-foreground">{-days === 1 ? 'in 1 day' : `in ${-days} days`}</span>
  );
}

/**
 * Dashboard (spec §12): stat tiles, then the work queues in priority order, then the last 20
 * activity entries across the workspace. Everything comes from one `GET /dashboard` call.
 */
export function DashboardPage() {
  const query = useQuery({
    queryKey: queryKeys.dashboard,
    queryFn: () => api.get<Dashboard>('/dashboard'),
    staleTime: 60_000,
  });
  const loading = query.isLoading;
  const d = query.data ?? EMPTY_DASHBOARD;
  const counts = d.counts;

  return (
    <>
      <PageHeader
        title="Dashboard"
        description={
          query.data?.generatedAt
            ? `Generated ${formatDateTime(query.data.generatedAt)}`
            : 'Chase list, urgent positions, follow-ups and recent activity.'
        }
        actions={
          <Button variant="outline" onClick={() => void query.refetch()} loading={query.isFetching}>
            <RefreshCw aria-hidden="true" /> Refresh
          </Button>
        }
      />

      {query.isError ? (
        <EmptyState
          icon={AlertTriangle}
          title="The dashboard could not be loaded"
          description={query.error instanceof Error ? query.error.message : 'Unknown error'}
          action={
            <Button variant="outline" onClick={() => void query.refetch()}>
              Try again
            </Button>
          }
          className="mb-6"
        />
      ) : null}

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Key figures" role="group">
        <StatCard
          label="Open positions"
          value={counts.openPositions}
          icon={Briefcase}
          to="/positions"
          loading={loading}
        />
        <StatCard
          label="Active candidates"
          value={counts.activeCandidates}
          icon={Users}
          to="/candidates"
          loading={loading}
        />
        <StatCard
          label="Submissions in progress"
          value={counts.submissionsInProgress}
          icon={Send}
          to="/submissions"
          loading={loading}
        />
        <StatCard
          label="Needs chase"
          value={counts.needsChase}
          icon={BellRing}
          to="/submissions?needsChase=true"
          tone={counts.needsChase > 0 ? 'destructive' : 'default'}
          hint={counts.needsChase > 0 ? 'Due today or overdue' : 'Nothing overdue'}
          loading={loading}
        />
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-2">
        <DashboardSection
          title="Chase list"
          description="Submissions with feedback or a follow-up due today or overdue."
          icon={BellRing}
          count={d.chaseList.length}
          loading={loading}
          tone="danger"
          emptyIcon={CheckCircle2}
          emptyTitle="Nothing to chase"
          emptyDescription="No feedback or follow-up is due today."
          link={{ to: '/submissions?needsChase=true', label: 'All submissions' }}
          className="xl:col-span-2"
        >
          <SectionTable caption="Submissions to chase">
            <SectionHead>
              <Th>Submission</Th>
              <Th>Waiting on</Th>
              <Th>Next step</Th>
              <Th>Due</Th>
              <Th className="text-right">Overdue</Th>
            </SectionHead>
            <tbody>
              {d.chaseList.map((s) => (
                <tr key={s.id} className="border-t">
                  <Td>
                    <EntityLink to={`/submissions/${s.id}`}>{s.title}</EntityLink>
                    <div className="text-xs text-muted-foreground">
                      <EnumBadge def={SubmissionStatus} value={s.status} size="sm" />
                    </div>
                  </Td>
                  <Td>{s.waitingOn ? <EnumBadge def={WaitingOn} value={s.waitingOn} size="sm" /> : EMPTY}</Td>
                  <Td className="max-w-[16rem] truncate">
                    {s.nextStep ? (
                      <span title={s.nextStep}>{s.nextStep}</span>
                    ) : (
                      <span className="text-muted-foreground">{EMPTY}</span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {s.dueOn ? <time dateTime={s.dueOn}>{formatDate(s.dueOn)}</time> : EMPTY}
                  </Td>
                  <Td className="whitespace-nowrap text-right">
                    <OverdueBadge days={s.overdueDays} />
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="Urgent positions"
          description="Start date within the urgent window (settings)."
          icon={Hourglass}
          count={d.urgentPositions.length}
          loading={loading}
          tone="warning"
          emptyTitle="No urgent positions"
          emptyDescription="No open position starts within the urgent window."
          link={{ to: '/positions?urgentOnly=true', label: 'Positions' }}
        >
          <SectionTable caption="Urgent positions">
            <SectionHead>
              <Th>Position</Th>
              <Th>Stage</Th>
              <Th>Start</Th>
              <Th className="text-right">Days</Th>
            </SectionHead>
            <tbody>
              {d.urgentPositions.map((p) => (
                <tr key={p.id} className="border-t">
                  <Td>
                    <EntityLink to={`/positions/${p.id}`}>{positionLabel(p)}</EntityLink>
                  </Td>
                  <Td>
                    <EnumBadge def={PositionStage} value={p.stage} size="sm" />
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {p.startDate ? <time dateTime={p.startDate}>{formatDate(p.startDate)}</time> : EMPTY}
                  </Td>
                  <Td
                    className={cn(
                      'whitespace-nowrap text-right tabular-nums',
                      p.daysToStart !== null && p.daysToStart < 0 && 'text-destructive',
                    )}
                  >
                    {p.daysToStart === null
                      ? EMPTY
                      : p.daysToStart < 0
                        ? `${-p.daysToStart} ago`
                        : p.daysToStart}
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="Closed on the portal but still active"
          description="The portal shows the request as closed while we still have it in progress."
          icon={ShieldAlert}
          count={d.closedOnPortalButActive.length}
          loading={loading}
          tone="warning"
          emptyTitle="Portal and pipeline agree"
          emptyDescription="No active position is closed on the portal."
        >
          <SectionTable caption="Positions closed on the portal but active">
            <SectionHead>
              <Th>Position</Th>
              <Th>Stage</Th>
              <Th>Portal</Th>
              <Th>Checked</Th>
              <Th className="text-right">Open subs.</Th>
            </SectionHead>
            <tbody>
              {d.closedOnPortalButActive.map((p) => (
                <tr key={p.id} className="border-t">
                  <Td>
                    <EntityLink to={`/positions/${p.id}`}>{positionLabel(p)}</EntityLink>
                  </Td>
                  <Td>
                    <EnumBadge def={PositionStage} value={p.stage} size="sm" />
                  </Td>
                  <Td>
                    <EnumBadge def={PortalStatusDetail} value={p.portalStatusDetail} size="sm" />
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {p.portalCheckedAt ? formatDate(p.portalCheckedAt) : EMPTY}
                  </Td>
                  <Td className="text-right tabular-nums">{p.openSubmissions}</Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="Sourcing without a JD"
          description="Positions at Sourcing that have no job description yet."
          icon={FileWarning}
          count={d.sourcingWithoutJd.length}
          loading={loading}
          tone="warning"
          emptyTitle="Every sourcing position has a JD"
        >
          <SectionTable caption="Positions at Sourcing without a JD">
            <SectionHead>
              <Th>Position</Th>
              <Th>Start</Th>
              <Th className="text-right" />
            </SectionHead>
            <tbody>
              {d.sourcingWithoutJd.map((p) => (
                <tr key={p.id} className="border-t">
                  <Td>
                    <EntityLink to={`/positions/${p.id}`}>{positionLabel(p)}</EntityLink>
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {p.startDate ? <time dateTime={p.startDate}>{formatDate(p.startDate)}</time> : EMPTY}
                  </Td>
                  <Td className="text-right">
                    <Link
                      to={`/positions/${p.id}`}
                      className={buttonVariants({ variant: 'outline', size: 'sm' })}
                    >
                      <Sparkles aria-hidden="true" /> Generate JD
                    </Link>
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="Waiting on us"
          description="Submissions in progress where the next step is ours."
          icon={UserRound}
          count={d.waitingOnUs.length}
          loading={loading}
          tone="warning"
          emptyTitle="Nothing is waiting on us"
          link={{ to: '/submissions?waitingOn=US', label: 'Submissions' }}
        >
          <SectionTable caption="Submissions waiting on us">
            <SectionHead>
              <Th>Submission</Th>
              <Th>Status</Th>
              <Th>Next step</Th>
              <Th>Follow-up</Th>
            </SectionHead>
            <tbody>
              {d.waitingOnUs.map((s) => (
                <tr key={s.id} className="border-t">
                  <Td>
                    <EntityLink to={`/submissions/${s.id}`}>{s.title}</EntityLink>
                  </Td>
                  <Td>
                    <EnumBadge def={SubmissionStatus} value={s.status} size="sm" />
                  </Td>
                  <Td className="max-w-[16rem] truncate">
                    {s.nextStep ? (
                      <span title={s.nextStep}>{s.nextStep}</span>
                    ) : (
                      <span className="text-muted-foreground">{EMPTY}</span>
                    )}
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {s.followUpOn ? (
                      <>
                        <time dateTime={s.followUpOn}>{formatDate(s.followUpOn)}</time>
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({formatRelativeDays(s.followUpOn)})
                        </span>
                      </>
                    ) : (
                      EMPTY
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="GDPR consents expiring"
          description="Consent runs out within the next 30 days."
          icon={ShieldAlert}
          count={d.consentsExpiring.length}
          loading={loading}
          tone="warning"
          emptyTitle="No consent expires soon"
          link={{ to: '/candidates', label: 'Candidates' }}
        >
          <SectionTable caption="Candidates whose consent expires soon">
            <SectionHead>
              <Th>Candidate</Th>
              <Th>Label</Th>
              <Th>Expires</Th>
            </SectionHead>
            <tbody>
              {d.consentsExpiring.map((c) => (
                <tr key={c.id} className="border-t">
                  <Td>
                    <EntityLink to={`/candidates/${c.id}`}>{c.displayName}</EntityLink>
                    <div className="text-xs text-muted-foreground">{c.candidateId}</div>
                  </Td>
                  <Td className="max-w-[14rem] truncate">
                    {c.label ?? <span className="text-muted-foreground">{EMPTY}</span>}
                  </Td>
                  <Td className="whitespace-nowrap tabular-nums">
                    {c.consentExpiry ? (
                      <>
                        <time dateTime={c.consentExpiry}>{formatDate(c.consentExpiry)}</time>
                        <span className="ml-1 text-xs text-muted-foreground">
                          ({formatRelativeDays(c.consentExpiry)})
                        </span>
                      </>
                    ) : (
                      EMPTY
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="Broken Drive links"
          description="Files whose Drive link no longer resolves (last link check)."
          icon={Link2Off}
          count={d.brokenLinks.length}
          loading={loading}
          tone="danger"
          emptyTitle="All Drive links are fine"
          link={{ to: '/settings/drive', label: 'Drive settings' }}
        >
          <SectionTable caption="Broken Drive links">
            <SectionHead>
              <Th>File</Th>
              <Th>Kind</Th>
              <Th>Record</Th>
              <Th className="text-right" />
            </SectionHead>
            <tbody>
              {d.brokenLinks.map((f) => (
                <tr key={f.id} className="border-t">
                  <Td className="max-w-[16rem] truncate">
                    <span title={f.name}>{f.name}</span>
                  </Td>
                  <Td>
                    <EnumBadge def={FileKind} value={f.kind} size="sm" />
                  </Td>
                  <Td>
                    {f.entityPath ? (
                      <EntityLink to={f.entityPath}>{f.entityLabel ?? f.entityId}</EntityLink>
                    ) : (
                      <span className="text-muted-foreground">
                        {f.entityLabel ?? `${f.entityType} ${f.entityId}`}
                      </span>
                    )}
                  </Td>
                  <Td className="text-right">
                    {f.webViewLink ? (
                      <a
                        href={f.webViewLink}
                        target="_blank"
                        rel="noreferrer"
                        aria-label={`Open ${f.name} in Drive`}
                        className={buttonVariants({ variant: 'ghost', size: 'icon-sm' })}
                      >
                        <ExternalLink aria-hidden="true" />
                      </a>
                    ) : null}
                  </Td>
                </tr>
              ))}
            </tbody>
          </SectionTable>
        </DashboardSection>

        <DashboardSection
          title="Recent activity"
          description="The last 20 entries across positions, candidates, submissions and partners."
          icon={History}
          count={d.recentActivity.length}
          loading={loading}
          emptyTitle="No activity yet"
          emptyDescription="Notes and automatic entries will show up here."
          className="xl:col-span-2"
        >
          <ol className="relative space-y-4 border-l pl-5">
            {d.recentActivity.map((entry) => {
              const auto = entry.kind === 'AUTO';
              return (
                <li key={entry.id} className="relative">
                  <span
                    className={cn(
                      'absolute -left-[1.45rem] top-1.5 flex h-4 w-4 items-center justify-center rounded-full border bg-background',
                      auto ? 'text-muted-foreground' : 'text-primary',
                    )}
                    aria-hidden="true"
                  >
                    {auto ? <Bot className="h-2.5 w-2.5" /> : <UserRound className="h-2.5 w-2.5" />}
                  </span>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                    {entry.entityPath ? (
                      <EntityLink to={entry.entityPath} className="text-xs">
                        {entry.entityLabel ?? entry.entityId}
                      </EntityLink>
                    ) : (
                      <span>{entry.entityLabel ?? entry.entityType}</span>
                    )}
                    <span>·</span>
                    <span>{auto ? 'Automatic' : (entry.actor ?? 'Note')}</span>
                  </div>
                  <div className="prose prose-sm max-w-none text-foreground dark:prose-invert prose-p:my-0 prose-ul:my-1 prose-li:my-0">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                    >{`**${formatDate(entry.date)}** – ${entry.text}`}</ReactMarkdown>
                  </div>
                </li>
              );
            })}
          </ol>
        </DashboardSection>
      </div>
    </>
  );
}
